import { getOriginalFileProcessingPath } from "@packages/commons";
import { type Event, type FileProcessingMetadata, normalizeEvent } from "@packages/commons";

export const eventDetailsUrl = (source: "model" | "tenancy" | "session", eventId: string, parentId?: string) => {
  const id = encodeURIComponent(eventId).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
  );
  return source === "tenancy" ? `auth/events/${id}` : `${source === "model" ? "models" : "sessions"}/${encodeURIComponent(parentId || "")}/events/${id}`;
};

export const readEventDetails = (response: any): Event => {
  // Unwrap transport responses without discarding a legacy event's data payload.
  const isEvent = (value: any) =>
    value &&
    typeof value === "object" &&
    (value.eventType ||
      value.messageType ||
      value.event ||
      typeof value.status === "string");
  const event = isEvent(response) ? response : (response?.data ?? response);
  if (!event || Array.isArray(event) || !isEvent(event)) {
    throw new Error("The event detail endpoint did not return an event. Check that the events API is deployed.");
  }
  const normalized = normalizeEvent(event);
  if (!normalized) {
    throw new Error("The event detail endpoint did not return an event. Check that the events API is deployed.");
  }
  return normalized;
};

const filePathFact = (event: Event, key: string): string | undefined => {
  const value = event.facts?.find(fact => fact.key === key)?.value;
  return typeof value === "string" && value.trim() ? value : undefined;
};

/** The `action` query parameter that opens a linked file's reprocess confirmation in Monitor → Files. */
export const REPROCESS_FILE_ACTION = "reprocess";

/** A Monitor → Files link that opens a project's file, optionally with its reprocess confirmation. */
export const monitorFileUrl = (projectId: string, path: string, action?: typeof REPROCESS_FILE_ACTION): string =>
  `/monitor/files?${new URLSearchParams({ projectId, filePath: path, ...(action ? { action } : {}) })}`;

const filesUrl = (event: Event, path: unknown, action?: typeof REPROCESS_FILE_ACTION): string | undefined => {
  if (!event.modelId || typeof path !== "string" || !path.trim()) return undefined;
  return monitorFileUrl(event.modelId, path, action);
};

/**
 * A failed-row file, or a retry file staged from one (`<name>.retry-<timestamp>.csv`) that failed as a whole. The
 * timestamp is ISO 8601 with dashes (`2026-09-30T10-15-00Z`), or the older compact form (`20260930T101500Z`).
 */
const FAILED_ROWS_FILE = /(\.retry-(?:\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}Z|\d{8}T\d{6}Z))?(\.errors)?\.csv$/i;

/**
 * Archived files with rejected rows retry their failed-row report. In the error folder, a failed-row or retry
 * file reprocesses just its rows; any other file reprocesses the whole file.
 */
export const getFileReprocessMode = (file: { path?: string; status?: string; processing?: FileProcessingMetadata }): "file" | "failedRows" | undefined => {
  if (!file.path || !file.processing) return undefined;
  if (file.status === "processed") {
    const counts = file.processing;
    if (counts?.wholeFile) return "file";
    const interruptedRemaining = counts?.outcome === "interrupted" && (counts.totalRows === undefined || counts.successfulRows === undefined || counts.successfulRows < counts.totalRows);
    return interruptedRemaining || counts?.failedRows || counts?.unattemptedRows ? "failedRows" : undefined;
  }
  if (file.status !== "error") return undefined;
  return file.processing?.wholeFile || !file.path.toLowerCase().endsWith(".csv") ? "file" : "failedRows";
};

/** Where a processed file's rejected rows wait in the error folder: `<scope>/error/<name>.errors.csv`. */
export const getFailedRowsPathForProcessedFile = (path: string, scope: string): string | undefined => {
  const marker = `/${scope}/processed/`;
  const index = path.indexOf(marker);
  if (index < 0) return undefined;
  const relative = getOriginalFileProcessingPath(path.slice(index + marker.length)).replace(/\.[^./]+$/, "");
  return `${path.slice(0, index)}/${scope}/error/${relative}.errors.csv`;
};

/** The original filename that a failed-row or retry file's rows are routed as, e.g. `EP_jobs.csv`. */
export const getFailedRowsOriginalName = (fileName: string): string => fileName.replace(FAILED_ROWS_FILE, ".csv");

export const eventFileUrl = (event: Event): string | undefined => {
  const factPath = filePathFact(event, "file-path") || filePathFact(event, "error-path") || filePathFact(event, "processed-path");
  // Legacy metadata.path can be an API route on other event types.
  const legacyPath = event.eventType?.startsWith("fileProcessing.")
    ? event.meta?.errorPath || event.meta?.processedPath || event.meta?.path
    : undefined;
  return filesUrl(event, factPath || legacyPath);
};

export const eventErrorFileUrl = (event: Event): string | undefined => {
  if (event.eventType !== "fileProcessing.failedRows") return undefined;
  return filesUrl(event, filePathFact(event, "csv-error-path") || event.meta?.csvErrorPath);
};

/** A link to the failed file (or failed-row file) an outcome event left in an error folder, ready to reprocess. */
export const eventReprocessLink = (event: Event): { label: string; url: string } | undefined => {
  if (event.eventType === "fileProcessing.failed") {
    const url = filesUrl(event, filePathFact(event, "error-path") || event.meta?.errorPath, REPROCESS_FILE_ACTION);
    return url ? { label: "Reprocess file", url } : undefined;
  }
  if (event.eventType === "fileProcessing.failedRows") {
    const csvErrorPath = filePathFact(event, "csv-error-path") || event.meta?.csvErrorPath;
    if (csvErrorPath) {
      const url = filesUrl(event, csvErrorPath, REPROCESS_FILE_ACTION);
      return url ? { label: "Reprocess failed rows", url } : undefined;
    }
    // When no row succeeded the whole file is in the error folder, without a failed-row file beside it.
    const url = filesUrl(event, filePathFact(event, "error-path") || event.meta?.errorPath, REPROCESS_FILE_ACTION);
    return url ? { label: "Reprocess file", url } : undefined;
  }
  return undefined;
};
